export enum ExpirationStatus {
  None = 'none',
  Two_Week = 'two_week',
  Week = 'week',
  Two_Day = 'two_day',
  One_Day = 'one_day',
  Expired = 'expired'
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const WARNING_THRESHOLD_DAYS = 14;
const WEEK_THRESHOLD_DAYS = 7;
const TWO_DAY_THRESHOLD_DAYS = 1;
const ONE_DAY_THRESHOLD_DAYS = 0;

/**
 * Used to make sure expiration time is at the end of the expiration day
 * @param date The date to normalize.
 */
function startOfExpirationDay(date: Date): number {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/**
 * Whole calendar days between `now` and the expiration date, or null if there is none.
 * @param expirationDate ISO date string the item expires on, or null/undefined if it has none.
 * @param now The current time to compare against.
 */
function getDaysRemaining(expirationDate: string | null | undefined, now: Date): number | null {
  return expirationDate
    ? Math.round(
        (startOfExpirationDay(new Date(expirationDate)) - startOfExpirationDay(now)) / MS_PER_DAY
      )
    : null;
}

/**
 * Classifies how close an expiration date is to the current date/time
 * @param expirationDate ISO date string the item expires on, or null/undefined if it has none.
 * @param now The current time to compare against. Defaults to `new Date()`.
 */
export function getExpirationStatus(
  expirationDate: string | null | undefined,
  now: Date = new Date()
): ExpirationStatus {
  const daysRemaining = getDaysRemaining(expirationDate, now);

  if (daysRemaining === null || daysRemaining > WARNING_THRESHOLD_DAYS) {
    return ExpirationStatus.None;
  }
  if (daysRemaining < 0) {
    return ExpirationStatus.Expired;
  }
  if (daysRemaining <= ONE_DAY_THRESHOLD_DAYS) {
    return ExpirationStatus.One_Day;
  }
  if (daysRemaining <= TWO_DAY_THRESHOLD_DAYS) {
    return ExpirationStatus.Two_Day;
  }
  if (daysRemaining <= WEEK_THRESHOLD_DAYS) {
    return ExpirationStatus.Week;
  }
  return ExpirationStatus.Two_Week;
}

/**
 * Helper method to add styling for when a document is within a week of expiration.
 * @param expirationDate ISO date string the item expires on, or null/undefined if it has none.
 * @param now The current time to compare against. Defaults to `new Date()`.
 */
export function isExpiringSoon(
  expirationDate: string | null | undefined,
  now: Date = new Date()
): boolean {
  const daysRemaining = getDaysRemaining(expirationDate, now);
  return daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= WEEK_THRESHOLD_DAYS;
}

/**
 * Days remaining until expiration (0 = expires today), used for tooltips.
 * @param expirationDate ISO date string the item expires on, or null/undefined if it has none.
 * @param now The current time to compare against. Defaults to `new Date()`.
 */
export function getDaysToExpiration(
  expirationDate: string | null | undefined,
  now: Date = new Date()
): number {
  return getDaysRemaining(expirationDate, now) ?? 0;
}
